import React, { useState } from 'react';
import { apiService } from '../../services/api';

function MarkdownImportModal({ graphId, onClose, onImported }) {
  const [markdown, setMarkdown] = useState('');
  const [parsed, setParsed] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setMarkdown(text);
      setParsed([]);
      setError('');
    } catch (e) {
      setError(`Failed to read file: ${e.message}`);
    }
  };

  const handlePreview = async () => {
    if (!markdown.trim()) {
      setError('Paste markdown or upload a file first.');
      return;
    }
    setPreviewing(true);
    setError('');
    try {
      const resp = await apiService.importGraphTestCasesFromMarkdown(graphId, markdown, { commit: false });
      if (!resp.success) throw new Error(resp.error || 'Preview failed');
      setParsed(resp.parsed || []);
      if (!resp.parsed || resp.parsed.length === 0) {
        setError(resp.message || 'No Q&A Set blocks were found in the markdown.');
      }
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Preview failed';
      setError(msg);
      setParsed([]);
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (parsed.length === 0) {
      setError('Run preview first to verify what will be imported.');
      return;
    }
    setCommitting(true);
    setError('');
    try {
      const resp = await apiService.importGraphTestCasesFromMarkdown(graphId, markdown, { commit: true });
      if (!resp.success) throw new Error(resp.error || 'Import failed');
      onImported(resp.committed || [], resp.count || 0);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Import failed';
      setError(msg);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="tc-modal-backdrop" onClick={onClose}>
      <div className="tc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tc-modal-header">
          <h2>Import Test Cases from Markdown</h2>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={committing}>
            Close
          </button>
        </div>
        <div className="tc-modal-body">
          <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>
            Expected format: each test case begins with{' '}
            <code>## Q&amp;A Set N: Title</code>, followed by{' '}
            <code>### Question</code> and <code>### Expected Answer</code> blocks. Trailing
            sections (Traceability Matrix, etc.) are ignored.
          </p>

          <div className="tc-form-field">
            <label>Upload .md file (optional)</label>
            <input type="file" accept=".md,.markdown,.txt" onChange={handleFile} />
          </div>

          <div className="tc-form-field">
            <label>Or paste markdown</label>
            <textarea
              className="large"
              value={markdown}
              onChange={(e) => { setMarkdown(e.target.value); setParsed([]); }}
              placeholder="## Q&A Set 1: ..."
            />
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          {parsed.length > 0 && (
            <>
              <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>
                Preview: {parsed.length} test case{parsed.length === 1 ? '' : 's'} ready to import
              </div>
              <div className="import-preview">
                {parsed.map((tc, i) => (
                  <div className="import-preview-row" key={i}>
                    <div className="title">{i + 1}. {tc.title}</div>
                    <div className="preview"><strong>Q:</strong> {tc.question}</div>
                    <div className="preview"><strong>A:</strong> {tc.expected_answer}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="tc-modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={committing}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={handlePreview}
            disabled={previewing || committing || !markdown.trim()}
          >
            {previewing ? 'Parsing...' : 'Preview'}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleImport}
            disabled={committing || parsed.length === 0}
          >
            {committing ? 'Importing...' : `Import ${parsed.length || ''} case${parsed.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MarkdownImportModal;
